import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faCopy, faPenToSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CircularProgress,
  Dialog,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import {
  BasePageQuotation,
  DefaultButton,
  Footer,
  Modal,
  NetworkIndicator,
  SearchableDropDown,
  backgroundThemes,
  colors,
  themeMethods,
} from 'design-system';
import { XCircle } from 'react-feather';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SendEmailIcon } from 'src/components/Icons';
import ProfileIcon from 'src/components/Icons/ProfileIcon';
import useProfile from 'src/hooks/useProfile/useProfile';
import SubAgentItem from 'src/models/Quotation/SubAgentItem';
import { GetPersonalAgentLink } from 'src/models/responseTypes/PersonalAgentLink';
import {
  createPersonalAgentLink,
  getPersonalAgentLink,
  updatePersonalAgentLink,
} from 'src/store/personalAgentLink/actions/actions';
import { getLicenses } from 'src/store/profile/actions';
import { getSubagents } from 'src/store/quotation/actions';
import { RootState } from 'src/store/rootReducer';

const blue = 'linear-gradient(180deg, #CEE8FF 10.19%, #FFFFFF 100%)';
const image = themeMethods.getCurrentMonthTheme(
  new Date(),
  backgroundThemes.LOGIN_PAGE,
);
export const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      flexFlow: 'column',
      flexGrow: 1,
      backgroundImage: blue,
      '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        width: '100%',
        height: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '0% 150%',
        backgroundSize: 'cover',
        backgroundImage: image,
        zIndex: -1,
        opacity: 0.2,
        [theme.breakpoints.down('sm')]: {
          backgroundImage: blue,
          opacity: 1,
        },
      },
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 20,
      paddingLeft: 12,
    },
    modalContentWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      maxWidth: 530,
      margin: 'auto',
      gap: 20,
    },
    contentBodyText: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4.57px',
    },
    contentBody: {
      backgroundColor: 'white',
      padding: '20px 24px',
      borderRadius: '10px',
    },
    selectInputDisabled: { display: 'none' },
    h4: {
      fontWeight: 900,
      fontSize: '19px',
      lineHeight: '24px',
      letterSpacing: '0.5px',
    },
    body1: {
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '20px',
      letterSpacing: '0.1px',
      color: colors.abbey,
    },
    alert: {
      backgroundColor: colors.onahau,
      color: colors.madison,
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '22px',
      letterSpacing: '0.1px',
      borderRadius: '10px',
    },
    textFieldIcons: {
      display: 'flex',
      gap: 10,
      color: colors.madison,
      fontWeight: 900,
    },
    textFieldDisabled: {
      color: '#7E8084',
    },
    faCircleInfo: {
      color: colors.madison,
    },
    centerButton: {
      display: 'flex',
      justifyContent: 'center',
    },
    popover: {
      marginLeft: '14px',
    },
    popoverText: {
      fontWeight: 500,
      fontSize: '12px',
      padding: '4px 8px',
    },
    modal: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '34px',
      padding: '42px 38px',
    },
    modalTextField: {
      maxWidth: '400px',
      width: '100%',
    },
  }),
);

export const mapProfileStateToProps = (
  state: RootState,
): {
  licenses: GeneralCatalogItem[];
  subAgentList: SubAgentItem[];
  personalLink: GetPersonalAgentLink;
} => ({
  licenses: state.profile.licenses,
  subAgentList: state.quotation.subAgentList,
  personalLink: state.personalLink,
});

const reduxActions = {
  getLicenses,
  getSubagents,
  getPersonalAgentLink,
  createPersonalAgentLink,
  updatePersonalAgentLink,
};
const connector = connect(mapProfileStateToProps, reduxActions);
export type ProfileScreenPropsRedux = ConnectedProps<typeof connector>;

export const ProfileScreen = (props: ProfileScreenPropsRedux): JSX.Element => {
  const history = useNavigate();
  const classes = useStyles();
  const {
    error,
    message,
    baseLink,
    anchorEl,
    isMobile,
    userLink,
    openModal,
    enableEdit,
    enableIcons,
    subAgentList,
    loadingSubmit,
    networkStatus,
    handleCopyLink,
    disabledButton,
    selectSubAgent,
    handleEnableEdit,
    handleFormSubmit,
    disabledDropdown,
    handleCloseModal,
    handleSubAgentChange,
    handleUserLinkChange,
  } = useProfile(props);

  return (
    <Dialog data-testid="quotation-form-id" open fullScreen>
      <Modal
        data-testid="modal"
        maxWidth="sm"
        open={openModal}
        title="¡Cambio realizado!"
        onCloseRequested={handleCloseModal}
        wrapperStyles={classes.modal}
      >
        <SendEmailIcon />
        <Typography
          variant="h4"
          align="center"
          className={classes.modalTextField}
        >
          Tu link personalizado <strong>{`${baseLink}${userLink}`}</strong> se
          ha creado exitosamente, te hemos enviado un correo confirmando el
          cambio.
        </Typography>
      </Modal>
      <div className={classes.root}>
        <BasePageQuotation
          data-testid="base-page"
          backgroundColor="#CEE8FF"
          stepperItem={
            <div className={classes.header}>
              <ProfileIcon />
              <Typography className={classes.h4} variant="h4">
                Mi perfil
              </Typography>
            </div>
          }
          rightActions={
            <IconButton
              data-testid="close-button-dashboard"
              onClick={(): void => history('/')}
            >
              <XCircle />
            </IconButton>
          }
        >
          <form onSubmit={handleFormSubmit} data-testid="form-profile">
            <div className={classes.modalContentWrapper}>
              <div className={classes.contentBodyText}>
                <Typography className={classes.h4} variant="h4">
                  Personaliza el url del cotizador
                </Typography>
                <Typography className={classes.body1} variant="body1">
                  Añade tu marca al link del cotizador. Ten en cuenta que este
                  cambio se debe de hacer de manera personal. Te recomendamos no
                  modificar el link más de <strong>dos veces.</strong>
                </Typography>
              </div>
              <div
                role="none"
                className={`${classes.contentBody} ${
                  disabledDropdown && classes.selectInputDisabled
                }`}
              >
                {networkStatus === 'success' && subAgentList.length > 0 && (
                  <SearchableDropDown
                    inputId="sales-web-dropdown-agent"
                    inputName="subAgent"
                    inputLabel="¿Quién aquí? Selecciona un subagente"
                    options={subAgentList}
                    defaultValue={selectSubAgent}
                    onChange={handleSubAgentChange}
                    optimized
                  />
                )}
                <NetworkIndicator
                  data-testid="loading"
                  message="Solicitando lista de subagentes..."
                  requesting={networkStatus !== 'success'}
                />
              </div>
              {(selectSubAgent || disabledDropdown) && (
                <>
                  <div className={classes.contentBody}>
                    <TextField
                      className={
                        !!props.personalLink.personalLink && !enableEdit
                          ? classes.textFieldDisabled
                          : ''
                      }
                      data-testid="userLink"
                      fullWidth
                      name="userLink"
                      label="Tu url personal"
                      value={userLink ?? ''}
                      disabled={
                        !!props.personalLink.personalLink && !enableEdit
                      }
                      onChange={handleUserLinkChange}
                      error={error}
                      helperText={message}
                      InputProps={{
                        startAdornment: baseLink,
                        endAdornment: enableIcons && (
                          <div className={classes.textFieldIcons}>
                            <Tooltip title="Editar url">
                              <FontAwesomeIcon
                                cursor="pointer"
                                onClick={handleEnableEdit}
                                icon={faPenToSquare}
                              />
                            </Tooltip>
                            <Tooltip title="Copiar url">
                              <FontAwesomeIcon
                                aria-owns="mouse-over-popover"
                                icon={faCopy}
                                onClick={handleCopyLink}
                                cursor="pointer"
                              />
                            </Tooltip>
                            <Popover
                              className={classes.popover}
                              anchorEl={anchorEl}
                              open={!!anchorEl}
                              anchorOrigin={{
                                vertical: 'center',
                                horizontal: 'right',
                              }}
                              transformOrigin={{
                                vertical: 'center',
                                horizontal: 'left',
                              }}
                            >
                              <Typography className={classes.popoverText}>
                                ¡URL copiado!
                              </Typography>
                            </Popover>
                          </div>
                        ),
                      }}
                    />
                  </div>
                  <Alert
                    className={classes.alert}
                    severity="info"
                    icon={
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={classes.faCircleInfo}
                      />
                    }
                  >
                    Te enviaremos un correo confirmando el cambio de url
                    realizado.
                  </Alert>
                </>
              )}
              <div className={classes.centerButton}>
                <DefaultButton
                  disabled={disabledButton || loadingSubmit || error}
                  forcedWidth={240}
                  type="submit"
                >
                  {!loadingSubmit && 'Guardar'}
                  {loadingSubmit && (
                    <CircularProgress size={20} data-testid="button-loading" />
                  )}
                </DefaultButton>
              </div>
            </div>
          </form>
        </BasePageQuotation>
        <Footer
          rootStyles={{ borderTopColor: 'transparent', maxHeight: '89px' }}
          IsMobile={isMobile}
          country="Costa Rica"
          linkText="Consulte nuestros términos y condiciones"
          linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
        />
      </div>
    </Dialog>
  );
};

export default connector(ProfileScreen);
